import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError, type AuthenticationResult } from '@azure/msal-browser';
import { Observable, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';

const GRAPH_PHOTO_ENDPOINT = 'https://graph.microsoft.com/v1.0/me/photo/$value';
const GRAPH_SCOPES = ['User.Read'];

/**
 * Retrieves user profile data directly from Microsoft Graph.
 * Token acquisition and HTTP calls are handled entirely in the frontend —
 * the ARES API is never involved in Graph communication.
 */
@Injectable({ providedIn: 'root' })
export class GraphService {
    private _msalService = inject(MsalService);

    /**
     * HttpClient backed by HttpBackend so Graph requests bypass the ARES API
     * auth interceptor (which only attaches tokens to ARES API calls).
     */
    private _http = new HttpClient(inject(HttpBackend));

    /**
     * Acquires a Microsoft Graph token silently and fetches the logged-in user's
     * profile photo from GET /me/photo/$value.
     *
     * Returns:
     * - A data URL string (data:image/jpeg;base64,...) on success.
     * - null when the user has no photo (404) or token acquisition fails.
     *
     * Never throws — all errors are caught and logged.
     */
    getProfilePhoto(): Observable<string | null> {
        const account = this._msalService.instance.getActiveAccount()
            ?? this._msalService.instance.getAllAccounts()[0]
            ?? null;

        if (!account) {
            return of(null);
        }

        return this._msalService
            .acquireTokenSilent({ account, scopes: GRAPH_SCOPES })
            .pipe(
                switchMap((result: AuthenticationResult) =>
                    this._http.get(GRAPH_PHOTO_ENDPOINT, {
                        headers: { Authorization: `Bearer ${result.accessToken}` },
                        responseType: 'blob',
                    }),
                ),
                switchMap((blob: Blob) => this._blobToDataUrl(blob)),
                catchError((err: unknown) => this._handlePhotoError(err)),
            );
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    /**
     * Converts a Blob (JPEG image) to a base64 data URL using the FileReader API.
     */
    private _blobToDataUrl(blob: Blob): Observable<string> {
        return new Observable<string>(observer => {
            const reader = new FileReader();

            reader.onload = (): void => {
                observer.next(reader.result as string);
                observer.complete();
            };

            reader.onerror = (): void => {
                observer.error(reader.error);
            };

            reader.readAsDataURL(blob);
        });
    }

    /**
     * Central error handler for Graph token acquisition and photo retrieval.
     *
     * - InteractionRequiredAuthError: silent token cannot be obtained without user
     *   interaction. For a non-critical resource like a profile photo we fall back
     *   gracefully rather than triggering a full loginRedirect that would navigate
     *   away from the user's current work. A loginRedirect is only appropriate when
     *   the user's primary session has expired.
     * - 404: the account has no profile photo — expected, not an error.
     * - 401 / 403: Graph permission not consented or token is invalid.
     * - Other HTTP errors: transient failure, logged as a warning.
     */
    private _handlePhotoError(err: unknown): Observable<null> {
        if (err instanceof InteractionRequiredAuthError) {
            console.warn(
                '[GraphService] Interaction required to acquire Graph token. ' +
                'Profile photo will not be displayed.',
            );
            return of(null);
        }

        if (err instanceof HttpErrorResponse) {
            if (err.status === 404) {
                console.info('[GraphService] User has no Microsoft profile photo.');
            } else if (err.status === 401 || err.status === 403) {
                console.warn(
                    `[GraphService] Graph returned ${err.status}. ` +
                    'Ensure "User.Read" is consented in the Azure App Registration.',
                );
            } else {
                console.warn('[GraphService] Failed to retrieve profile photo:', err.status, err.message);
            }
            return of(null);
        }

        console.warn('[GraphService] Unexpected error fetching profile photo:', err);
        return of(null);
    }
}
